/**
 * Dashboard Statistics Component
 * 
 * Displays job stats, upcoming jobs, and recent failures.
 */
import React from 'react';

const DashboardStats = ({ dashboard, onRefresh }) => {
  const { job_stats, recent_runs, upcoming_jobs, recent_failures } = dashboard;

  // Format next run time
  const formatNextRun = (dateStr) => {
    if (!dateStr) return 'Not scheduled';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = date - now;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 0) return 'Overdue';
    if (diffMins < 60) return `in ${diffMins}m`;
    if (diffMins < 1440) return `in ${Math.floor(diffMins / 60)}h`;
    return date.toLocaleDateString();
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Stat cards: one bg-bg-2 surface + a token accent, not a dark-only
            gradient with a hardcoded glow drop-shadow behind it. */}
        <div className="bg-bg-2 rounded-xl p-5 border border-[var(--divider)] shadow-lg relative overflow-hidden group hover:border-accent/30 transition-all duration-300">
          <div className="absolute top-0 right-0 p-4 opacity-10 font-bold text-6xl transform group-hover:scale-110 transition-transform duration-300 text-fg-3">#</div>
          <div className="relative z-10">
            <div className="text-3xl font-bold text-accent">
              {job_stats.total_jobs}
            </div>
            <div className="text-sm font-medium text-fg-2 mt-1">Total Jobs</div>
            <div className="text-xs text-fg-2 mt-2 flex gap-2">
              <span className="text-profit">{job_stats.enabled_jobs} active</span>
              <span className="text-fg-3">•</span>
              <span className="text-fg-2">{job_stats.disabled_jobs} inactive</span>
            </div>
          </div>
        </div>

        <div className="bg-bg-2 rounded-xl p-5 border border-[var(--divider)] shadow-lg relative overflow-hidden group hover:border-profit/30 transition-all duration-300">
          <div className="absolute top-0 right-0 p-4 opacity-10 text-profit font-bold text-6xl transform group-hover:scale-110 transition-transform duration-300">✓</div>
          <div className="relative z-10">
            <div className="text-3xl font-bold text-profit">
              {recent_runs.successful_runs_24h}
            </div>
            <div className="text-sm font-medium text-fg-2 mt-1">Successful (24h)</div>
            <div className="text-xs text-fg-2 mt-2">
              {recent_runs.total_runs_24h > 0 
                ? `${((recent_runs.successful_runs_24h / recent_runs.total_runs_24h) * 100).toFixed(0)}% success rate`
                : 'No movement yet'}
            </div>
          </div>
        </div>

        <div className="bg-bg-2 rounded-xl p-5 border border-[var(--divider)] shadow-lg relative overflow-hidden group hover:border-loss/30 transition-all duration-300">
          <div className="absolute top-0 right-0 p-4 opacity-10 text-loss font-bold text-6xl transform group-hover:scale-110 transition-transform duration-300">!</div>
          <div className="relative z-10">
            <div className="text-3xl font-bold text-loss">
              {recent_runs.failed_runs_24h}
            </div>
            <div className="text-sm font-medium text-fg-2 mt-1">Failed (24h)</div>
            <div className="text-xs text-fg-2 mt-2">
               Action required for failures
            </div>
          </div>
        </div>

        {/* --badge-warn-*, not text-warning/NN: `warning` is a plain var() with no
            <alpha-value>, so an opacity modifier on it silently emits nothing. */}
        <div className="bg-bg-2 rounded-xl p-5 border border-[var(--divider)] shadow-lg relative overflow-hidden group hover:border-[var(--divider)] transition-all duration-300">
          <div className="absolute top-0 right-0 p-4 opacity-10 text-[var(--badge-warn-fg)] font-bold text-6xl transform group-hover:scale-110 transition-transform duration-300">⏱</div>
          <div className="relative z-10">
            <div className="text-3xl font-bold text-[var(--badge-warn-fg)] flex items-baseline gap-1">
              {recent_runs.average_duration_ms
                ? (recent_runs.average_duration_ms / 1000).toFixed(2)
                : '0'}
              <span className="text-sm font-normal text-fg-2">sec</span>
            </div>
            <div className="text-sm font-medium text-fg-2 mt-1">Avg Duration</div>
            <div className="text-xs text-fg-2 mt-2">
              Time per execution
            </div>
          </div>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upcoming Jobs */}
        <div className="bg-bg-2/50 border border-[var(--divider)] rounded-xl p-6 shadow-lg backdrop-blur-sm">
          <div className="flex items-center justify-between mb-6">
            <h3 className="ds-h3 text-fg-1 flex items-center gap-2">
              <span className="text-accent">⏰</span> Upcoming Jobs
            </h3>
            <button
              type="button"
              onClick={onRefresh}
              className="btn btn-ghost btn-sm"
            >
              Refresh
            </button>
          </div>
          
          {upcoming_jobs.length === 0 ? (
            <div className="text-fg-3 text-center py-8 flex flex-col items-center">
              <span className="text-3xl mb-2 opacity-50">💤</span>
              No upcoming jobs
            </div>
          ) : (
            <div className="space-y-3">
              {upcoming_jobs.map((job) => (
                <div
                  key={job.id}
                  className="flex items-center justify-between gap-3 bg-bg-1/50 border border-[var(--divider)] rounded-lg p-4 transition-colors group"
                >
                  {/* min-w-0 + truncate: without them the font-mono
                      "service → endpoint" line refused to shrink and crushed the
                      cron chip on the right into a letter-per-line sliver. */}
                  <div className="min-w-0">
                    <div className="truncate font-semibold text-fg-2 group-hover:text-accent transition-colors">
                      {job.job_name}
                    </div>
                    <div className="truncate text-xs text-fg-2 mt-1 font-mono">
                      {job.target_service} <span className="text-fg-3">→</span> {job.target_endpoint}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm text-accent font-bold">
                      {formatNextRun(job.next_run_at)}
                    </div>
                    <div className="text-[10px] text-fg-2 mt-1 bg-bg-2 px-2 py-0.5 rounded-full border border-[var(--divider)] inline-block">
                      {job.cron_expression}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Failures */}
        <div className="bg-bg-2/50 border border-[var(--divider)] rounded-xl p-6 shadow-lg backdrop-blur-sm">
          <h3 className="ds-h3 text-fg-1 mb-6 flex items-center gap-2">
            <span className="text-loss">❌</span> Recent Failures
          </h3>
          
          {recent_failures.length === 0 ? (
            <div className="text-center py-8 flex flex-col items-center">
              <div className="w-12 h-12 bg-[var(--badge-profit-bg)] rounded-full flex items-center justify-center mb-3">
                <span className="text-xl">✅</span>
              </div>
              <span className="text-fg-2 font-medium">All systems operational</span>
              <span className="text-xs text-fg-2 mt-1">No failures in the last 24 hours</span>
            </div>
          ) : (
            <div className="space-y-3">
              {recent_failures.map((run) => (
                <div
                  key={run.id}
                  className="bg-bg-2 border border-[var(--divider)] rounded-lg p-4 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="truncate font-medium text-fg-1">{run.job_name}</div>
                      <div className="text-xs text-fg-2 mt-0.5">
                        {new Date(run.started_at).toLocaleString()}
                      </div>
                    </div>
                    <span className="ds-badge xs loss shrink-0 font-mono">
                      ID: {run.id.substring(0, 6)}
                    </span>
                  </div>

                  {run.error_message && (
                    <div className="mt-2 text-xs text-[var(--badge-loss-fg)] bg-[var(--badge-loss-bg)] p-2 rounded border border-[var(--divider)] font-mono break-all">
                      {run.error_message}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Job Types Distribution */}
      <div className="bg-bg-2/50 border border-[var(--divider)] rounded-xl p-6 shadow-lg backdrop-blur-sm">
        <h3 className="ds-h3 text-fg-1 mb-4">
          📊 Jobs by Type
        </h3>
        <div className="flex flex-wrap gap-4">
          {Object.entries(job_stats.jobs_by_type || {}).map(([type, count]) => (
            <div 
              key={type}
              className="bg-bg-1/50 border border-[var(--divider)] rounded-lg px-5 py-3 flex items-center gap-4 min-w-[150px]"
            >
              <span className="text-2xl opacity-80">
                {type === 'http' ? '🌐' : type === 'celery' ? '🔧' : '☸️'}
              </span>
              <div>
                <div className="font-bold text-xl text-fg-1">{count}</div>
                <div className="text-xs text-fg-2 uppercase tracking-wider font-semibold">{type}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DashboardStats;
